/**
 * The only place in the repository that constructs an `Intl` formatter.
 *
 * `.docs/02-shared-core.md`, DRY watchlist: "`formatMoney` is the only
 * `Intl.NumberFormat` call in the repo." `money.ts`, `dates.ts` and `format.ts` all
 * come through here, so there is one call site, one cache, and one rule about the
 * minus sign.
 *
 * Not exported from the package barrel: it is how the four formatters are built,
 * not something a consumer formats with.
 */
/**
 * U+2212 MINUS SIGN, which is what the mockups render (`−$560.00`, `−15%`).
 * `Intl` emits U+002D HYPHEN-MINUS in `en-US` and `en-GB`; every formatter here
 * swaps it.
 */
export const MINUS_SIGN = '−';
const numberFormats = new Map();
const dateTimeFormats = new Map();
const pluralRulesByLocale = new Map();
/**
 * Constructing an `Intl` formatter is expensive and a portal page formats a few
 * hundred amounts; the options objects passed in are literals, so their key order
 * is stable and a miss only ever costs one construction.
 */
function keyOf(locale, options) {
    return `${locale}|${JSON.stringify(options)}`;
}
export function numberFormat(locale, options) {
    const key = keyOf(locale, options);
    const cached = numberFormats.get(key);
    if (cached !== undefined)
        return cached;
    const created = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, created);
    return created;
}
export function dateTimeFormat(locale, options) {
    const key = keyOf(locale, options);
    const cached = dateTimeFormats.get(key);
    if (cached !== undefined)
        return cached;
    const created = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, created);
    return created;
}
export function pluralRules(locale) {
    const cached = pluralRulesByLocale.get(locale);
    if (cached !== undefined)
        return cached;
    const created = new Intl.PluralRules(locale);
    pluralRulesByLocale.set(locale, created);
    return created;
}
/** Formats a number and replaces the hyphen-minus with a real minus sign. */
export function formatWithMinusSign(formatter, value) {
    return formatter
        .formatToParts(value)
        .map((part) => (part.type === 'minusSign' ? MINUS_SIGN : part.value))
        .join('');
}
/** Reads one part out of a `formatToParts` result. */
export function partOf(parts, type) {
    return parts.find((part) => part.type === type)?.value ?? '';
}
//# sourceMappingURL=intl.js.map